import React, { useEffect, useRef, useState } from 'react';
import { Card, Button, Spinner } from 'react-bootstrap';
// eslint-disable-next-line import/no-extraneous-dependencies
import mapboxgl from 'mapbox-gl';
import { getRoute, getMapboxToken } from '../Services/MapboxService';

// Set Mapbox access token
mapboxgl.accessToken = getMapboxToken();

/**
 * MapRoutePanel Component
 * Displays a Mapbox map with route between pickup and dropoff locations
 *
 * @param {Object} props
 * @param {Object} props.fromCoords - Origin coordinates {lat, lon}
 * @param {Object} props.toCoords - Destination coordinates {lat, lon}
 * @param {string} props.fromAddress - Origin address for display
 * @param {string} props.toAddress - Destination address for display
 * @param {Array} props.stops - Array of intermediate stops [{lat, lon, address, stop_type}]
 * @param {Function} props.onRouteCalculated - Callback with {distance, duration}
 * @param {boolean} props.isCollapsed - Whether panel is collapsed
 * @param {Function} props.onToggleCollapse - Toggle collapse callback
 */
function MapRoutePanel({
  fromCoords,
  toCoords,
  fromAddress,
  toAddress,
  stops = [],
  onRouteCalculated,
  isCollapsed,
  onToggleCollapse,
}) {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [routeInfo, setRouteInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const markersRef = useRef([]);

  // Serialize stops to prevent unnecessary re-renders from array reference changes
  const stopsKey = JSON.stringify(
    stops.map((s) => ({ lat: s.latitude, lon: s.longitude, type: s.stop_type })),
  );

  // Check if we have valid coordinates
  const hasValidCoords = fromCoords?.lat && fromCoords?.lon
    && toCoords?.lat && toCoords?.lon;

  // Track if map is ready for adding sources/layers
  const [mapReady, setMapReady] = useState(false);

  // Initialize map
  useEffect(() => {
    if (!hasValidCoords || isCollapsed) {
      return undefined;
    }

    // Clean up existing map if present
    if (map.current) {
      map.current.remove();
      map.current = null;
    }

    // Reset mapReady state for new map
    setMapReady(false);

    // Small delay for initial render
    const initTimeout = setTimeout(() => {
      if (!mapContainer.current) return;

      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: [
          (parseFloat(fromCoords.lon) + parseFloat(toCoords.lon)) / 2,
          (parseFloat(fromCoords.lat) + parseFloat(toCoords.lat)) / 2,
        ],
        zoom: 10,
      });

      map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

      // Set up load handler for new map
      map.current.on('load', () => {
        setMapReady(true);
        // Trigger resize after map loads to ensure it fills container
        if (map.current) {
          map.current.resize();
        }
      });
    }, 50);

    // Use ResizeObserver to detect container size changes (handles modal animations)
    let resizeObserver;
    if (mapContainer.current) {
      resizeObserver = new ResizeObserver(() => {
        if (map.current) {
          map.current.resize();
        }
      });
      resizeObserver.observe(mapContainer.current);
    }

    return () => {
      clearTimeout(initTimeout);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [hasValidCoords, isCollapsed]);

  // Get color for stop type
  const getStopColor = (stopType) => {
    switch (stopType) {
      case 'activity': return '#0d6efd'; // blue
      case 'break': return '#ffc107'; // yellow
      case 'waypoint':
      default: return '#17a2b8'; // info/teal
    }
  };

  // Get label for stop type
  const getStopLabel = (stopType) => {
    switch (stopType) {
      case 'activity': return 'Activity';
      case 'break': return 'Break';
      case 'waypoint':
      default: return 'Waypoint';
    }
  };

  // Update markers and route when coordinates change AND map is ready
  useEffect(() => {
    if (!map.current || !hasValidCoords || isCollapsed || !mapReady) return;

    const updateMapRoute = async () => {
      setLoading(true);

      // Clear existing markers
      markersRef.current.forEach((marker) => marker.remove());
      markersRef.current = [];

      // Add pickup marker (green)
      const pickupMarker = new mapboxgl.Marker({ color: '#28a745' })
        .setLngLat([parseFloat(fromCoords.lon), parseFloat(fromCoords.lat)])
        .setPopup(new mapboxgl.Popup().setHTML(
          `<strong>Pickup</strong>${fromAddress ? `<br/><small>${fromAddress}</small>` : ''}`,
        ))
        .addTo(map.current);
      markersRef.current.push(pickupMarker);

      // Add intermediate stop markers
      const validStops = stops.filter((s) => s.latitude && s.longitude);
      validStops.forEach((stop, index) => {
        const stopMarker = new mapboxgl.Marker({ color: getStopColor(stop.stop_type) })
          .setLngLat([parseFloat(stop.longitude), parseFloat(stop.latitude)])
          .setPopup(new mapboxgl.Popup().setHTML(
            `<strong>${getStopLabel(stop.stop_type)} #${index + 1}</strong>${stop.address ? `<br/><small>${stop.address}</small>` : ''}${stop.notes ? `<br/><em>${stop.notes}</em>` : ''}`,
          ))
          .addTo(map.current);
        markersRef.current.push(stopMarker);
      });

      // Add dropoff marker (red)
      const dropoffMarker = new mapboxgl.Marker({ color: '#dc3545' })
        .setLngLat([parseFloat(toCoords.lon), parseFloat(toCoords.lat)])
        .setPopup(new mapboxgl.Popup().setHTML(
          `<strong>Drop-off</strong>${toAddress ? `<br/><small>${toAddress}</small>` : ''}`,
        ))
        .addTo(map.current);
      markersRef.current.push(dropoffMarker);

      // Build waypoints for route calculation (if we have valid stops)
      const waypoints = validStops.map((s) => ({ lat: s.latitude, lon: s.longitude }));

      // Fetch route (with waypoints if available)
      const route = await getRoute(fromCoords, toCoords, waypoints);

      if (route && map.current) {
        setRouteInfo({
          distance: route.distance,
          duration: route.duration,
        });

        // Notify parent
        if (onRouteCalculated) {
          onRouteCalculated({
            distance: route.distance,
            duration: route.duration,
          });
        }

        // Remove existing route layer if present
        try {
          if (map.current.getLayer('route')) {
            map.current.removeLayer('route');
          }
          if (map.current.getSource('route')) {
            map.current.removeSource('route');
          }

          // Add route to map
          map.current.addSource('route', {
            type: 'geojson',
            data: {
              type: 'Feature',
              properties: {},
              geometry: route.geometry,
            },
          });

          map.current.addLayer({
            id: 'route',
            type: 'line',
            source: 'route',
            layout: {
              'line-join': 'round',
              'line-cap': 'round',
            },
            paint: {
              'line-color': '#3b82f6',
              'line-width': 5,
              'line-opacity': 0.8,
            },
          });

          // Fit map to route bounds
          const { coordinates } = route.geometry;
          const bounds = coordinates.reduce(
            (b, coord) => b.extend(coord),
            new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]),
          );

          map.current.fitBounds(bounds, {
            padding: 50,
          });
        } catch (err) {
          // Style may not be ready, will retry on next render
          // eslint-disable-next-line no-console
          console.warn('Map style not ready:', err.message);
        }
      }

      setLoading(false);
    };

    updateMapRoute();
  }, [
    fromCoords?.lat,
    fromCoords?.lon,
    toCoords?.lat,
    toCoords?.lon,
    stopsKey,
    isCollapsed,
    mapReady,
  ]);

  // Don't render anything if no valid coordinates
  if (!hasValidCoords) {
    return null;
  }

  return (
    <Card className="map-route-panel h-100">
      <Card.Header
        className="d-flex justify-content-between align-items-center py-2"
        style={{ cursor: 'pointer' }}
        onClick={onToggleCollapse}
      >
        <div className="d-flex align-items-center">
          <i className="fa fa-map me-2" />
          <span className="fw-semibold">Route Map</span>
        </div>
        <Button
          variant="link"
          size="sm"
          className="p-0 text-decoration-none"
        >
          {isCollapsed ? (
            <>
              <i className="fa fa-chevron-left me-1" />
              Show
            </>
          ) : (
            <>
              Hide
              <i className="fa fa-chevron-right ms-1" />
            </>
          )}
        </Button>
      </Card.Header>

      {!isCollapsed && (
        <Card.Body className="p-0 position-relative">
          <div
            ref={mapContainer}
            className="map-container"
            style={{ height: '400px', width: '100%' }}
          />

          {/* Route info overlay */}
          {routeInfo && (
            <div
              className="route-info-overlay position-absolute bg-white rounded shadow-sm p-2"
              style={{ bottom: '10px', left: '10px', zIndex: 1 }}
            >
              <div className="d-flex gap-3">
                <div>
                  <small className="text-muted d-block">Distance</small>
                  <strong>
                    {routeInfo.distance}
                    {' '}
                    km
                  </strong>
                </div>
                <div>
                  <small className="text-muted d-block">Est. Duration</small>
                  <strong>
                    {routeInfo.duration}
                    {' '}
                    min
                  </strong>
                </div>
              </div>
            </div>
          )}

          {/* Loading overlay */}
          {loading && (
            <div
              className="position-absolute top-50 start-50 translate-middle"
              style={{ zIndex: 2 }}
            >
              <Spinner animation="border" variant="primary" />
            </div>
          )}
        </Card.Body>
      )}
    </Card>
  );
}

export default MapRoutePanel;
